import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Workflow, WorkflowDocument } from '../../types/workflowTypes';
import { formatCurrency } from '../../utils/formatters';
import { formatDate, getStatusTranslation } from '../../utils/helpers';
import { updateWorkflowTitle } from '../../api/workflowApi';
import { useIsReadOnly } from '../../hooks/useIsReadOnly';
import Badge, { BadgeTone } from '../common/Badge';

/**
 * Workflow-Status → Badge-Ton (WP-L1, qa-ux-theme-v5-light.md §2.2).
 * Ersetzt das frühere `getWorkflowStatusColor`, das rohe Hex-Werte als
 * className interpolierte (ungültige Klasse → Status-Pille rendert ohne
 * Hintergrund in beiden Themes). Mapping erhält die bisherige Farbsemantik:
 * completed=grün→success, in_progress=blau→info, draft=orange→caution,
 * cancelled=rot→danger, Rest=grau→neutral.
 */
const getWorkflowStatusTone = (status: string): BadgeTone => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'in_progress':
      return 'info';
    case 'draft':
      return 'caution';
    case 'cancelled':
      return 'danger';
    default:
      return 'neutral';
  }
};

// Hilfsfunktion für sichere Datumswerte
const safeDate = (date: any): Date => {
  if (!date) return new Date();
  if (date && typeof date.toDate === 'function') {
    const d = date.toDate();
    if (d instanceof Date && !isNaN(d.getTime())) return d;
  }
  if (date instanceof Date && !isNaN(date.getTime())) return date;
  if (typeof date === 'string' || typeof date === 'number') {
    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) return parsedDate;
  }
  console.warn('[WFCard] Ungültiger Datumswert:', date);
  return new Date();
};

// Hilfsfunktion für sichere numerische Werte
const safeNumber = (value: any): number => {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number' && !isNaN(value)) return value;
  if (typeof value === 'string') {
    try {
      const cleanValue = value.replace(/[^\d.,]/g, '');
      const normalizedValue = cleanValue.includes('.') && cleanValue.includes(',') 
        ? cleanValue.replace(/\./g, '').replace(',', '.')
        : cleanValue.replace(',', '.');
      const parsed = parseFloat(normalizedValue);
      if (!isNaN(parsed)) return parsed;
    } catch (e) {
      console.warn('[WFCard] Fehler beim Parsen des String-Wertes:', value, e);
      return 0;
    }
  }
  return 0;
};

interface WorkflowCardProps {
  workflow: Workflow;
}

const WorkflowCard: React.FC<WorkflowCardProps> = ({ workflow }) => {
  const { t } = useTranslation('workflow');
  const navigate = useNavigate();
  // M11b-3: advisor (read-only) darf den Workflow-Titel nicht bearbeiten.
  const isReadOnly = useIsReadOnly();

  // States für das Inline-Editing
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(workflow.name || '');
  const [isSaving, setIsSaving] = useState(false);

  // Übersetzung für Workflow-Typen
  const getWorkflowTypeLabel = (type: string): string => {
    switch (type) {
      case 'invoice_lifecycle':
        return t('detail.workflowTypes.invoiceLifecycle');
      case 'offer_approval':
        return t('detail.workflowTypes.offerApproval');
      case 'document_review':
        return t('detail.workflowTypes.documentReview');
      default:
        return t('detail.workflowTypes.default');
    }
  };

  // Übersetzung für Dokumenttypen
  const getDocumentTypeLabel = (type: string): string => {
    switch (type) {
      case 'invoice':
        return t('detail.documentTypes.invoice');
      case 'offer':
        return t('detail.documentTypes.offer');
      case 'xrechnung':
        return t('detail.documentTypes.xrechnung');
      case 'down_payment':
        return t('detail.documentTypes.downPayment');
      case 'final_invoice':
        return t('detail.documentTypes.finalInvoice');
      case 'credit_note':
        return t('detail.documentTypes.creditNote');
      case 'reminder':
        return t('detail.documentTypes.reminder');
      default:
        return t('detail.documentTypes.document');
    }
  };

  // Übersetzung für verknüpfte Entitäten
  const getRelatedEntityLabel = (type: string): string => {
    switch (type) {
      case 'invoice':
        return t('detail.documentTypes.invoice');
      case 'offer':
        return t('detail.documentTypes.offer');
      case 'contract':
        return t('detail.relatedEntity.contract');
      default:
        return t('detail.relatedEntity.document');
    }
  };

  // Lokale Status-Übersetzungsfunktion für i18n
  const getLocalStatusTranslation = (status?: string): string => {
    if (!status) return t('detail.common.unknown');
    
    const statusLower = status.toLowerCase();
    
    switch (statusLower) {
      case 'draft': return t('card.status.draft');
      case 'sent': return t('card.status.sent');
      case 'accepted': return t('card.status.accepted');
      case 'invoiced': return t('card.status.invoiced');
      case 'paid': return t('card.status.paid');
      case 'overdue': return t('card.status.overdue');
      case 'rejected': return t('card.status.rejected');
      case 'canceled': return t('card.status.canceled');
      case 'issued': return t('card.status.issued');
      case 'completed': return t('card.status.completed');
      case 'partially_paid': return t('card.status.partiallyPaid');
      case 'pending_approval': return t('card.status.pendingApproval');
      case 'approved': return t('card.status.approved');
      case 'expired': return t('card.status.expired');
      case 'revised': return t('card.status.revised');
      case 'pending_submission': return t('card.status.pendingSubmission');
      case 'received': return t('card.status.received');
      case 'custom': return t('card.status.custom');
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };
  
  // Berechne den Titel basierend auf den Prioritäten aus der API-Spezifikation
  const title = workflow.name || 
                (workflow.customer?.companyName || workflow.customer?.personName || t('detail.common.unknownCustomer'));
  
  // Workflow-Status
  const status = workflow.status || 'draft';
  const statusLabel = getLocalStatusTranslation(status);
  const statusTone = getWorkflowStatusTone(status);
  
  // Workflow-Typ bestimmen (neu)
  const type = workflow.type || workflow.processType || 'default';
  const typeLabel = getWorkflowTypeLabel(type);
  
  // Aktuelle Dokumente nach Datum sortieren, neueste zuerst
  const sortedDocuments: WorkflowDocument[] = [...(workflow.documents || [])].sort((a, b) => {
    const dateA = a.date ? safeDate(a.date).getTime() : 0;
    const dateB = b.date ? safeDate(b.date).getTime() : 0;
    return dateB - dateA; // Neueste zuerst
  });
  
  // Berechnung des Gesamtwerts
  const calculateTotalValue = (): number => {
    // Priorisiere den Gesamtbetrag aus workflow.metadata oder workflow.totalAmount
    if (workflow.totalAmount) {
      return safeNumber(workflow.totalAmount);
    }
    
    if (workflow.metadata && workflow.metadata.totalAmount) {
      return safeNumber(workflow.metadata.totalAmount);
    }
    
    // Fallback auf Dokumenten-Logik
    if (!workflow.documents || workflow.documents.length === 0) return 0;

    // Berücksichtige Rechnungen, Angebote, XRechnungen, Abschlagsrechnungen und Abschlussrechnungen
    const relevantDocuments = workflow.documents.filter(
      doc => doc.type === 'invoice' || doc.type === 'offer' || doc.type === 'xrechnung' || 
             doc.type === 'down_payment' || doc.type === 'final_invoice'
    );

    if (relevantDocuments.length > 0) {
      return relevantDocuments.reduce((sum, doc) => sum + safeNumber(doc.totalAmount), 0);
    }
    
    return 0;
  };
  
  const totalValue = calculateTotalValue();
  const displayCurrency = workflow.currency || workflow.metadata?.currency || workflow.latestDocument?.currency || 'EUR';

  // Zeige die letzten 2 Dokumente an
  const displayDocuments = sortedDocuments.slice(0, 2);
  const hasMoreDocuments = sortedDocuments.length > 2;
  
  // Funktionen für das Inline-Editing
  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditedTitle(workflow.name || '');
  };

  const handleSaveTitle = async () => {
    if (editedTitle.trim() === workflow.name) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await updateWorkflowTitle(workflow.id, editedTitle.trim());
      setIsEditing(false);
      // Aktualisiere den Workflow im lokalen State (falls Parent-Komponente das nicht automatisch macht)
      workflow.name = editedTitle.trim();
    } catch (error) {
      console.error('Fehler beim Speichern des Titels:', error);
      // Hier könnte man eine Toast-Notification oder ähnliches zeigen
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedTitle(workflow.name || '');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Weiterleitung zur Detailseite
  const handleViewDetail = () => {
    navigate(`/dashboard/workflows/${workflow.id}`);
  };
  
  return (
    <div className="bg-surface-raised shadow-lg rounded-xl p-5 hover:shadow-xl transition-shadow border border-hairline h-full flex flex-col justify-between">
      <div> {/* Wrapper für den oberen Inhalt */} 
        {/* Header mit Titel und Status */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1 mr-2">
            <div className="flex items-center group">
              {isEditing ? (
                <div className="flex-1 flex items-center space-x-2">
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="flex-1 bg-surface-inset text-primary text-lg font-semibold px-2 py-1 rounded border border-strong focus:border-indigo-500 focus:outline-none"
                    autoFocus
                    disabled={isSaving}
                  />
                  <button
                    onClick={handleSaveTitle}
                    disabled={isSaving}
                    className="text-[var(--tone-success-fg)] hover:text-green-300 text-sm font-medium disabled:opacity-50"
                  >
                    {isSaving ? '...' : '✓'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="text-muted hover:text-secondary text-sm font-medium disabled:opacity-50"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-primary truncate flex-1" title={title}>{title}</h3>
                  {/* Titel bearbeiten — M11b-3: für advisor (read-only) ausgeblendet */}
                  {!isReadOnly && (
                  <button
                    onClick={handleEditClick}
                    className="opacity-0 group-hover:opacity-100 text-muted hover:text-primary text-sm ml-2 transition-opacity"
                    title={t('detail.editTitle')}
                  >
                    ✏️
                  </button>
                  )}
                </>
              )}
            </div>
            <p className="text-muted text-sm">{t('card.created')}: {formatDate(safeDate(workflow.createdAt))}</p>
          </div>
          <Badge tone={statusTone} className="whitespace-nowrap">
            {statusLabel}
          </Badge>
        </div>
        
        {/* Workflow-Metadaten */}
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <span className="text-muted">{t('card.type')}:</span>
            <p className="text-secondary">{typeLabel}</p>
          </div>
          <div>
            <span className="text-muted">{t('card.lastUpdate')}:</span>
            <p className="text-secondary">{formatDate(safeDate(workflow.updatedAt || workflow.createdAt))}</p>
          </div>
          <div>
            <span className="text-muted">{t('card.id')}:</span>
            <p className="text-secondary truncate" title={workflow.id}>{workflow.id.substring(0, 8)}</p>
          </div>
          <div>
            <span className="text-muted">{t('card.value')}:</span>
            <p className="font-semibold text-primary">
              {totalValue > 0 ? formatCurrency(totalValue, displayCurrency) : '-'}
            </p>
          </div>
        </div>
        
        {/* Beschreibung, falls vorhanden */}
        {workflow.description && (
          <div className="mb-4">
            <span className="text-muted text-sm">{t('card.description')}:</span>
            <p className="text-secondary text-sm line-clamp-2" title={workflow.description}>
              {workflow.description}
            </p>
          </div>
        )}
        
        {/* Verknüpfte Entität, falls vorhanden */}
        {workflow.relatedEntityId && (
          <div className="mb-4">
            <span className="text-muted text-sm">{t('card.relatedTo')}:</span>
            <p className="text-secondary text-sm">
              {getRelatedEntityLabel(workflow.relatedEntityType || 'document')} {workflow.relatedEntityId}
            </p>
          </div>
        )}
        
        {/* Dokumente */}
        <div className="mb-4">
          <h4 className="text-secondary text-sm mb-2">{t('card.documents')}:</h4>
          <div className="bg-surface-inset rounded-lg p-2 min-h-[50px]">
            {displayDocuments.length > 0 ? (
              displayDocuments.map((doc, index) => (
                <div key={doc.id || index} className="text-sm mb-1 flex justify-between text-secondary">
                  <span className="truncate">
                    {getDocumentTypeLabel(doc.type)} {doc.number || 'N/A'}
                  </span>
                  <span className="text-muted whitespace-nowrap ml-2">
                    {formatCurrency(safeNumber(doc.totalAmount), doc.currency || displayCurrency)}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-xs text-muted italic">{t('card.noDocuments')}</p>
            )}
            {hasMoreDocuments && (
              <div className="text-xs text-muted text-center mt-1">
                +{sortedDocuments.length - displayDocuments.length} {t('card.moreDocuments')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer mit Aktionsbutton */}
      <div className="flex justify-end text-sm border-t border-hairline pt-3">
        <button 
          onClick={handleViewDetail}
          className="bg-indigo-600 hover:bg-indigo-700 text-[#0C1322] font-medium py-1.5 px-3 rounded transition"
        >
          {t('card.showDetails')}
        </button>
      </div>
    </div>
  );
};

export default WorkflowCard; 